import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Clock, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import TimeTrackLogo from "../components/TimeTrackLogo";

export default function Login() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("admin@lomtimetrack.com");
  const [password, setPassword] = useState("admin123");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  React.useEffect(() => {
    if (user) nav("/home");
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) {
      setError(res.error);
      toast.error(res.error);
      return;
    }
    toast.success(`Welcome back, ${res.user.name}`);
    nav("/home");
  };

  const quickLogin = (em, pw) => {
    setEmail(em);
    setPassword(pw);
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-zinc-50">
      {/* Left: hero panel */}
      <div className="relative hidden md:flex flex-col justify-between p-12 bg-zinc-950 text-white overflow-hidden">
        <div className="relative z-10">
          <TimeTrackLogo size={44} variant="lockup" invert />
        </div>

        <div className="relative z-10 space-y-6">
          <div className="label-eyebrow text-zinc-400">Workforce attendance system</div>
          <h1 className="font-heading font-black text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
            Right people.<br />
            Right place.<br />
            <span className="text-emerald-400">Right time.</span>
          </h1>
          <p className="text-zinc-400 max-w-md leading-relaxed">
            Track staff arrival, departure, lateness and working hours across locations — verified by GPS, classified in real time.
          </p>
        </div>

        <div className="relative z-10 grid grid-cols-3 gap-px border-t border-zinc-800 pt-6">
          <Stat label="GPS Verified" value="100%" />
          <Stat label="Roles" value="3" />
          <Stat label="Statuses" value="8" />
        </div>

        {/* decoration */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.07] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <div className="md:hidden mb-8">
            <TimeTrackLogo size={40} variant="lockup" />
          </div>

          <div className="label-eyebrow mb-2">Sign in</div>
          <h2 className="font-heading font-black text-4xl tracking-tighter text-zinc-950 mb-2">
            Welcome back
          </h2>
          <p className="text-zinc-600 mb-8">Use your work email and password to continue.</p>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="label-eyebrow block mb-2">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-white border border-zinc-300 rounded-sm focus:outline-none focus:border-zinc-950 transition-colors font-mono text-sm"
                placeholder="you@company.com"
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Password</label>
              <input
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-white border border-zinc-300 rounded-sm focus:outline-none focus:border-zinc-950 transition-colors font-mono text-sm"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div data-testid="login-error" className="px-4 py-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              data-testid="login-submit-btn"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-4 py-3.5 bg-zinc-950 hover:bg-zinc-800 disabled:opacity-50 text-white font-semibold rounded-sm transition-colors group"
            >
              <span>{loading ? "Signing in…" : "Sign in"}</span>
              <ArrowRight weight="bold" size={16} className="group-hover:translate-x-0.5 transition-transform" />
            </button>
          </form>

          <div className="mt-10 border-t border-zinc-200 pt-6">
            <div className="label-eyebrow mb-3">Demo accounts — tap to fill</div>
            <div className="grid grid-cols-1 gap-2">
              <QuickLogin
                tid="quick-login-admin"
                label="Admin"
                email="admin@lomtimetrack.com"
                onClick={() => quickLogin("admin@lomtimetrack.com", "admin123")}
              />
              <QuickLogin
                tid="quick-login-supervisor"
                label="Supervisor"
                email="supervisor@lomtimetrack.com"
                onClick={() => quickLogin("supervisor@lomtimetrack.com", "supervisor123")}
              />
              <QuickLogin
                tid="quick-login-employee"
                label="Employee"
                email="employee1@lomtimetrack.com"
                onClick={() => quickLogin("employee1@lomtimetrack.com", "employee123")}
              />
            </div>
          </div>

          <div className="mt-6 text-center text-sm text-zinc-600">
            New to LOM TimeTrack?{" "}
            <Link to="/signup" className="font-semibold text-zinc-950 underline" data-testid="login-to-signup-link">
              Sign up your company
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="px-4">
      <div className="font-heading font-black text-3xl tracking-tighter">{value}</div>
      <div className="text-[0.65rem] tracking-[0.22em] uppercase text-zinc-500 mt-1">{label}</div>
    </div>
  );
}

function QuickLogin({ label, email, onClick, tid }) {
  return (
    <button
      type="button"
      data-testid={tid}
      onClick={onClick}
      className="flex items-center justify-between px-4 py-2.5 border border-zinc-200 hover:border-zinc-950 hover:bg-zinc-50 rounded-sm transition-colors text-left group"
    >
      <div>
        <div className="text-xs label-eyebrow">{label}</div>
        <div className="text-sm text-zinc-700 font-mono">{email}</div>
      </div>
      <ArrowRight size={14} className="text-zinc-400 group-hover:text-zinc-950" />
    </button>
  );
}
